import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

/**
 * Roster (build plan §7.1 #2): the people read from the applications, and the
 * roster read itself. Reading is an explicit System Admin action ("Read
 * applications now"), or the 15-minute schedule when Config turns it on. It
 * never runs by itself at start-up, and it never writes to the applications.
 */

interface Person {
  personKey: string;
  personType: string;
  lastName: string;
  firstName: string;
  dateOfBirth: string;
  ageAtProgram: string;
  turns18On: string;
  reviewStatus: string;
  expectedReasons: string[];
  familyUpdatedAfterReview: boolean;
  sourceLastUpdatedUtc: string;
}

interface SweepResult {
  trigger: "manual" | "schedule";
  actorEmail: string;
  finishedAtUtc: string;
  durationMs: number;
  ok: boolean;
  error?: string;
  population: number;
  created: number;
  updated: number;
  unchanged: number;
  expected: number;
  failed: number;
  mismatches: { personKey: string; columns: string[] }[];
  refused: { personKey: string; reason: string }[];
  duplicates: string[];
  flagsUnreadable: string[];
  notInPopulation: string[];
  sessionDatesKnown: boolean;
}

interface SweepStatus {
  running: boolean;
  done: number;
  total: number;
  last: SweepResult | null;
}

const REASON_LABEL: Record<string, string> = {
  Meds: "Medication",
  SelfCarry: "Self-carry",
  Allergy: "Allergy",
  Adult18: "18+",
  Flagged: "Flagged",
  CheckinAnswer: "Check-in answer",
};
const REVIEW_LABEL: Record<string, string> = {
  NotReviewed: "To review",
  Reviewed: "Reviewed",
  ReReviewRequested: "Re-review requested",
  FamilyUpdated: "Family updated",
};

function eastern(iso: string, withTime = true): string {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "numeric", minute: "2-digit" } : { year: "numeric" }),
  }).format(t);
}
/** A calendar date (YYYY-MM-DD) shown without any time-zone shift. */
function calendar(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }).format(
    new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
  );
}

function LastRun({ r }: { r: SweepResult }) {
  const notes: string[] = [];
  if (!r.sessionDatesKnown) notes.push("Session dates are blank in Config, so 18-year-olds could not be worked out yet.");
  if (r.mismatches.length) notes.push(`${r.mismatches.length} changed a name, email or date of birth after their roster row was made (not changed here): ${r.mismatches.map((m) => m.personKey).join(", ")}.`);
  if (r.notInPopulation.length) notes.push(`${r.notInPopulation.length} on the roster are no longer submitted applications (kept): ${r.notInPopulation.join(", ")}.`);
  if (r.refused.length) notes.push(`${r.refused.length} application(s) could not be added (no reference number, or another program's).`);
  if (r.duplicates.length) notes.push(`More than one roster row for: ${r.duplicates.join(", ")}.`);
  if (r.flagsUnreadable.length) notes.push(`Flags unreadable (left alone) for: ${r.flagsUnreadable.join(", ")}.`);
  if (r.failed) notes.push(`${r.failed} row(s) could not be written; read again to retry.`);
  return (
    <div className="mt-3 text-sm">
      <p className={r.ok ? "text-slate-700" : "text-red-700"}>
        Last read {eastern(r.finishedAtUtc)} ({r.trigger === "schedule" ? "scheduled" : `by ${r.actorEmail}`}):{" "}
        {r.error ?? `${r.population} submitted applications · ${r.created} added · ${r.updated} updated · ${r.unchanged} unchanged · ${r.expected} expected at the med station.`}
      </p>
      {notes.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-900">
          {notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Roster(): React.ReactElement {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [status, setStatus] = useState<SweepStatus | null>(null);
  const [scheduled, setScheduled] = useState(false);
  const [canRun, setCanRun] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const wasRunning = useRef(false);

  const loadPeople = useCallback(async () => {
    const r = await api<{ people: Person[] }>("GET", "/api/roster");
    if (r.ok) setPeople(r.data.people);
    else setError(r.data.error ?? "Could not load the roster.");
  }, []);

  const loadStatus = useCallback(async () => {
    const r = await api<{ status: SweepStatus; scheduled: boolean; canRun: boolean }>("GET", "/api/roster/sweep");
    if (!r.ok) return;
    setStatus(r.data.status);
    setScheduled(r.data.scheduled);
    setCanRun(r.data.canRun);
    if (wasRunning.current && !r.data.status.running) void loadPeople();
    wasRunning.current = r.data.status.running;
  }, [loadPeople]);

  useEffect(() => {
    void loadPeople();
    void loadStatus();
  }, [loadPeople, loadStatus]);

  useEffect(() => {
    if (!status?.running) return;
    const id = window.setInterval(() => void loadStatus(), 1500);
    return () => window.clearInterval(id);
  }, [status?.running, loadStatus]);

  async function run() {
    setError(null);
    const r = await api<{ status: SweepStatus }>("POST", "/api/roster/sweep", {});
    if (!r.ok) {
      setError(r.data.error ?? "Could not start.");
      return;
    }
    wasRunning.current = true;
    setStatus(r.data.status);
  }

  const q = filter.trim().toLowerCase();
  const shown = (people ?? []).filter((p) => !q || `${p.lastName} ${p.firstName} ${p.personKey}`.toLowerCase().includes(q));

  return (
    <section>
      <h2 className="mb-4 text-xl font-semibold">Roster</h2>
      <div className="section-card">
        <p className="text-sm text-slate-700">
          The roster is read from the submitted applications. Reading never changes an application.{" "}
          {scheduled ? "It is also read automatically every 15 minutes." : "Automatic reading is off (Config › Roster schedule)."}
        </p>
        {canRun && (
          <button type="button" className="btn-primary mt-3 w-full sm:w-auto" onClick={run} disabled={status?.running}>
            {status?.running ? `Reading… ${status.total ? `${status.done} of ${status.total}` : ""}` : "Read applications now"}
          </button>
        )}
        {status?.running && !canRun && <p className="mt-3 text-sm text-slate-600" role="status">Reading the applications now…</p>}
        {error && <p className="field-error mt-2" role="alert">{error}</p>}
        {status?.last && !status.running && <LastRun r={status.last} />}
      </div>

      <div className="section-card">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <p className="text-sm text-slate-700">{people ? `${people.length} people` : "Loading…"}</p>
          <div>
            <label htmlFor="roster-filter" className="sr-only">Find a person</label>
            <input id="roster-filter" className="field-input w-56 max-w-full" placeholder="Find by name or reference" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        </div>
        {people && people.length === 0 && (
          <p className="text-sm text-slate-600">
            No one yet.{canRun ? " Read the applications to fill the roster." : " A System Admin reads the applications to fill it."}
          </p>
        )}
        {shown.length > 0 && (
          <>
            <ul className="divide-y divide-slate-200 md:hidden">
              {shown.map((p) => (
                <li key={p.personKey} className="py-3 text-sm">
                  <p className="font-medium">{p.lastName}, {p.firstName}</p>
                  <p className="text-slate-600">{p.personKey} · {REVIEW_LABEL[p.reviewStatus] ?? p.reviewStatus}{p.familyUpdatedAfterReview ? " · updated after review" : ""}</p>
                  {p.expectedReasons.length > 0 && <p className="text-slate-700">Med station: {p.expectedReasons.map((r) => REASON_LABEL[r] ?? r).join(", ")}</p>}
                  {p.turns18On && <p className="text-slate-700">Turns 18 on {calendar(p.turns18On)}</p>}
                </li>
              ))}
            </ul>
            <table className="hidden w-full text-left text-sm md:table">
              <thead className="border-b border-slate-200 text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Reference</th>
                  <th className="py-2 pr-3 font-medium">Review</th>
                  <th className="py-2 pr-3 font-medium">Expected at med station</th>
                  <th className="py-2 pr-3 font-medium">Turns 18</th>
                  <th className="py-2 font-medium">Application updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((p) => (
                  <tr key={p.personKey}>
                    <td className="py-2 pr-3">{p.lastName}, {p.firstName}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{p.personKey}</td>
                    <td className="py-2 pr-3">
                      {REVIEW_LABEL[p.reviewStatus] ?? p.reviewStatus}
                      {p.familyUpdatedAfterReview && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">updated after review</span>}
                    </td>
                    <td className="py-2 pr-3">{p.expectedReasons.map((r) => REASON_LABEL[r] ?? r).join(", ") || "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{p.turns18On ? calendar(p.turns18On) : "—"}</td>
                    <td className="py-2 whitespace-nowrap">{eastern(p.sourceLastUpdatedUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </section>
  );
}
